import { API_BASE } from '@/shared/services/api';
import { useState, useEffect, useCallback } from 'react';

const API_URL = API_BASE;

/**
 * Hook to check and manage mobile app permissions
 * Uses the access profiles system to determine which features a user can access
 */
export const useMobilePermissions = (appType = 'driver') => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disabledFeatures, setDisabledFeatures] = useState([]);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('ezerpToken');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/access-profiles/mobile/features?app_type=${appType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features);
        setDisabledFeatures(data.disabled_features || []);
        setError(null);
      } else {
        // Fallback: allow all features if API fails
        console.warn('Failed to fetch mobile permissions, using defaults');
        setFeatures({});
      }
    } catch (err) {
      console.error('Error fetching mobile permissions:', err);
      setError(err.message);
      // Fallback: allow all features
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [appType]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if a specific feature is enabled
   */
  const canAccess = useCallback((featureName) => {
    // If features not loaded yet or empty, allow by default
    if (Object.keys(features).length === 0) return true;
    
    const feature = features[featureName];
    return feature ? feature.enabled : true;
  }, [features]);

  /**
   * Check if user has a specific capability (for fine-grained control)
   */
  const checkCapability = useCallback(async (capabilityCode) => {
    const token = localStorage.getItem('ezerpToken');
    if (!token) return false;

    try {
      const response = await fetch(
        `${API_URL}/api/access-profiles/mobile/check?capability=${encodeURIComponent(capabilityCode)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        return data.allowed;
      }
    } catch (err) {
      console.error('Error checking capability:', err);
    }
    return true; // Default to allow if check fails
  }, []);

  return {
    features,
    loading,
    error,
    disabledFeatures,
    canAccess,
    checkCapability,
    refresh: fetchPermissions
  };
};

/**
 * Component wrapper to conditionally render based on permission
 */
export const MobileFeatureGate = ({ 
  feature, 
  appType = 'driver',
  fallback = null, 
  children 
}) => {
  const { canAccess, loading } = useMobilePermissions(appType);
  
  if (loading) return null;
  
  if (!canAccess(feature)) {
    return fallback;
  }
  
  return <>{children}</>;
};

/**
 * Hook for driver-specific permissions
 */
export const useDriverPermissions = () => {
  return useMobilePermissions('driver');
};

/**
 * Hook for mobile WMS permissions
 */
export const useMobileWMSPermissions = () => {
  return useMobilePermissions('mobile_wms');
};

export default useMobilePermissions;
